import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { T } from "./pdUtil";
import { CheckBox } from "./pdChart";
import { chartPreviewBulk, chartIngestSelective } from "@/lib/patientDocs";

// category -> [label, chart section] (per the ingest spec)
const SECTION = {
  demographics: ["Demographics", "Demographics"], insurance: ["Insurance", "Insurance"],
  problems: ["Problems", "Problem List"], medications: ["Medications", "Medications"],
  allergies: ["Allergies", "Allergies"], care_team: ["Care team", "Care Team"],
  visits: ["Visit", "Encounters"], visit_codes: ["Visit diagnoses", "Encounter diagnoses"],
  procedures: ["Procedures", "Encounter procedures"], vitals: ["Vitals", "Vitals"],
  labs: ["Lab results", "Labs"], imaging: ["Imaging reports", "Imaging"], immunizations: ["Immunizations", "Immunizations"],
};
const catLabel = (c) => (SECTION[c]?.[0] || c);
const catSection = (c) => (SECTION[c]?.[1] || c);

// Mirror of the server: each selected add-item counts to its category, plus each
// child to its own category except `visits` (the visit itself is the parent row).
function selectedCounts(items, sel) {
  const c = {};
  for (const it of items) {
    if (it.action !== "add" || !sel.has(it.iid)) continue;
    c[it.category] = (c[it.category] || 0) + 1;
    for (const ch of it.children || []) if (ch.category !== "visits") c[ch.category] = (c[ch.category] || 0) + 1;
  }
  return c;
}
const summarize = (counts) =>
  Object.entries(counts).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${catLabel(k).toLowerCase()}`).join(", ");

export default function ChartIngestDialog({ row, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pv, setPv] = useState(null);     // previews[0]
  const [sel, setSel] = useState(new Set());
  const [collapsed, setCollapsed] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const toggleCollapse = (cat) => setCollapsed((s) => { const n = new Set(s); n.has(cat) ? n.delete(cat) : n.add(cat); return n; });

  useEffect(() => {
    if (!row) return;
    let alive = true;
    setLoading(true); setErr(""); setPv(null);
    chartPreviewBulk([row.id]).then((data) => {
      if (!alive) return;
      const p = (data.previews || [])[0];
      if (!p) {
        const why = (data.skipped || data.errors || [])[0]?.reason || "There's nothing to add from this document.";
        setErr(why); setLoading(false); return;
      }
      setPv(p);
      setSel(new Set((p.items || []).filter((it) => it.action === "add").map((it) => it.iid)));
      setLoading(false);
    }).catch((e) => { if (alive) { setErr(e?.response?.data?.detail || "Couldn't read the chart preview."); setLoading(false); } });
    return () => { alive = false; };
  }, [row]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const it of pv?.items || []) {
      if (!g.has(it.category)) g.set(it.category, []);
      g.get(it.category).push(it);
    }
    return [...g.entries()];
  }, [pv]);

  const counts = useMemo(() => selectedCounts(pv?.items || [], sel), [pv, sel]);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const toggleItem = (iid) => setSel((s) => { const n = new Set(s); n.has(iid) ? n.delete(iid) : n.add(iid); return n; });
  const toggleCat = (items) => {
    const adds = items.filter((it) => it.action === "add").map((it) => it.iid);
    const allOn = adds.every((i) => sel.has(i));
    setSel((s) => { const n = new Set(s); adds.forEach((i) => (allOn ? n.delete(i) : n.add(i))); return n; });
  };

  const confirm = async () => {
    if (total === 0) return;
    setBusy(true);
    try {
      await chartIngestSelective([row.id], { [row.id]: [...sel] });
      toast.success(`Adding ${total} item${total === 1 ? "" : "s"} to the chart…`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to chart");
      setBusy(false);
    }
  };

  if (!row) return null;
  const pt = pv?.patient;

  return (
    <div data-testid="pd-ingest-dialog" className="pd-scrim" style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(20,20,18,.28)", display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pd-sheet" style={{ width: "min(420px,96vw)", height: "100%", display: "flex", flexDirection: "column", background: "#fff", borderLeft: `1px solid ${T.hair}`, overflow: "hidden", boxShadow: "-18px 0 60px rgba(20,20,18,.20)" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${T.hair}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>Add to chart{pt?.name ? ` · ${pt.name}` : ""}</div>
            <div style={{ fontSize: 12, color: T.sub, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {pt?.mrn ? `MRN ${pt.mrn} · ` : ""}{pv?.filename || "Reviewing…"}
            </div>
          </div>
          <button onClick={onClose} data-testid="pd-ingest-close" aria-label="Close" style={{ color: T.mute, background: "transparent", border: 0, cursor: "pointer" }}><X size={18} /></button>
        </div>

        {/* live totals */}
        {pv ? (
          <div data-testid="pd-ingest-totals" style={{ padding: "10px 20px", background: "#FAFAFA", borderBottom: `1px solid ${T.hair}`, fontSize: 12.5, color: T.ink }}>
            {total > 0 ? <>Will add <b>{summarize(counts)}</b></> : <span style={{ color: T.sub }}>Nothing selected — pick at least one item below.</span>}
          </div>
        ) : null}

        {/* body */}
        <div style={{ flex: 1, minHeight: 0, padding: "8px 20px 16px", overflowY: "auto" }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.sub, fontSize: 13, padding: "28px 0", justifyContent: "center" }}><Loader2 size={16} className="animate-spin" /> Reading chart data…</div>
          ) : err ? (
            <div data-testid="pd-ingest-error" style={{ fontSize: 13, color: T.red, background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 10, padding: "12px 14px", margin: "12px 0" }}>{err}</div>
          ) : (
            grouped.map(([cat, items]) => {
              const adds = items.filter((it) => it.action === "add");
              const on = adds.filter((it) => sel.has(it.iid)).length;
              const allOn = adds.length > 0 && on === adds.length;
              const isOpen = !collapsed.has(cat);
              return (
                <div key={cat} data-testid={`pd-ingest-cat-${cat}`} style={{ borderTop: `1px solid ${T.hair}`, padding: "16px 0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <CheckBox testid={`pd-ingest-cat-cb-${cat}`} checked={allOn} indeterminate={on > 0 && !allOn} onChange={() => toggleCat(items)} />
                    <button type="button" onClick={() => toggleCollapse(cat)} data-testid={`pd-ingest-cat-toggle-${cat}`} aria-expanded={isOpen}
                      style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, background: "transparent", border: 0, cursor: "pointer", padding: 0, textAlign: "left" }}>
                      <ChevronDown size={15} style={{ color: T.mute, flex: "none", transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform .15s ease" }} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{catLabel(cat)}</span>
                        <span style={{ fontSize: 11.5, color: T.sub }}> → {catSection(cat)}</span>
                      </span>
                    </button>
                    {adds.length ? <span style={{ fontSize: 11, color: T.sub, fontVariantNumeric: "tabular-nums" }}>{on}/{adds.length} selected</span> : null}
                  </div>
                  {isOpen ? (
                  <div style={{ paddingLeft: 32, marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                    {items.map((it, i) => it.action === "add" ? (
                      <label key={it.iid} data-testid={`pd-ingest-item-${it.iid}`} style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", fontSize: 12.5, color: T.ink }}>
                        <span style={{ marginTop: 1 }}><CheckBox testid={`pd-ingest-item-cb-${it.iid}`} checked={sel.has(it.iid)} onChange={() => toggleItem(it.iid)} /></span>
                        <span style={{ textDecoration: sel.has(it.iid) ? "none" : "line-through", color: sel.has(it.iid) ? T.ink : T.mute }}>
                          {it.label}
                          {(it.children || []).length ? <span style={{ color: T.sub }}> · {it.children.map((ch) => ch.label).join(" · ")}</span> : null}
                        </span>
                      </label>
                    ) : (
                      <div key={`skip${i}`} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, color: T.mute }}>
                        <span style={{ width: 16 }} />
                        <span>{it.label} <span style={{ fontSize: 11 }}>· already on chart</span></span>
                      </div>
                    ))}
                  </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        {/* footer */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderTop: `1px solid ${T.hair}` }}>
          <button onClick={onClose} data-testid="pd-ingest-cancel" style={{ fontSize: 13, fontWeight: 700, color: T.sub, background: "transparent", border: 0, cursor: "pointer" }}>Cancel</button>
          <span style={{ flex: 1 }} />
          <button onClick={confirm} disabled={busy || total === 0 || loading || !!err} data-testid="pd-ingest-confirm"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: (busy || total === 0 || loading || err) ? "#E4E4E1" : T.ink, color: (busy || total === 0 || loading || err) ? "#9B9B97" : "#fff", border: 0, borderRadius: 10, padding: "9px 18px", fontSize: 13, fontWeight: 800, cursor: (busy || total === 0 || loading || err) ? "default" : "pointer" }}>
            {busy ? <><Loader2 size={14} className="animate-spin" /> Adding…</> : `Add ${total} item${total === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
